import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { type Item, UnitType, validate as validateItem } from '../../models/Item';
import PageContainer from '../layout/PageContainer';
import { useLazyQuery, useMutation } from '@apollo/client/react';
import { useNavigate, useParams } from 'react-router-dom';
import ItemForm, { ItemFormState } from './ItemForm';
import useNotifications from '../../hooks/useNotifications/useNotifications';
import { FormFieldValue } from '../forms/FormFieldValue';
import { GET_ITEM_BY_ID_QUERY, GET_ITEMS_QUERY } from '../../queries/ItemQueries';
import { UPDATE_ITEM_MUTATION } from '../../mutations/ItemMutations';

function ItemEditForm({
  initialValues,
  onSubmit,
}: {
  initialValues: Partial<ItemFormState['values']>;
  onSubmit: (id: number, formValues: Partial<ItemFormState['values']>) => Promise<boolean>;
}) {
  const { itemId } = useParams();
  const navigate = useNavigate();

  const notifications = useNotifications();

  const [formState, setFormState] = useState<ItemFormState>(() => ({
    values: initialValues,
    errors: {},
  }));

  const formValues = formState.values;
  const formErrors = formState.errors;

  const setFormValues = useCallback(
    (newFormValues: Partial<ItemFormState['values']>) => {
      setFormState((previousState) => ({
        ...previousState,
        values: newFormValues,
      }));
    },
    [],
  );

  const setFormErrors = useCallback(
    (newFormErrors: Partial<ItemFormState['errors']>) => {
      setFormState((previousState) => ({
        ...previousState,
        errors: newFormErrors,
      }));
    },
    [],
  );

  const handleFormFieldChange = useCallback(
    (name: keyof ItemFormState['values'], value: FormFieldValue) => {
      const validateField = async (values: Partial<ItemFormState['values']>) => {
        const { issues } = validateItem(values);
        setFormErrors({
          ...formErrors,
          [name]: issues?.find((issue) => issue.path?.[0] === name)?.message,
        });
      };

      const newFormValues = { ...formValues, [name]: value };

      setFormValues(newFormValues);
      validateField(newFormValues);
    },
    [formValues, formErrors, setFormErrors, setFormValues],
  );

  const handleFormReset = useCallback(() => {
    setFormValues(initialValues);
  }, [initialValues, setFormValues]);

  const handleFormSubmit = useCallback(async () => {
    const { issues } = validateItem(formValues);

    if (issues && issues.length > 0) {
      setFormErrors(
        Object.fromEntries(issues.map((issue) => [issue.path?.[0], issue.message])),
      );
      return;
    }

    setFormErrors({});

    const id = +(itemId || '0');

    if (id > 0) {
      try {
        const success = await onSubmit(id, formValues);

        if (success) {
          notifications.show('Item edited successfully.', {
            severity: 'success',
            autoHideDuration: 3000,
          });

          navigate(`/dashboard/items/${id}`);
        } else {
          notifications.show(
            'Failed to edit item. Reason: Unknown.',
            {
              severity: 'error',
              autoHideDuration: 3000,
            },
          );
        }
      } catch (editError) {
        notifications.show(
          `Failed to edit item. Reason: ${(editError as Error).message}`,
          {
            severity: 'error',
            autoHideDuration: 3000,
          },
        );
        throw editError;
      }
    }
  }, [formValues, itemId, navigate, notifications, onSubmit, setFormErrors]);

  return (
    <ItemForm
      formState={formState}
      onFieldChange={handleFormFieldChange}
      onSubmit={handleFormSubmit}
      onReset={handleFormReset}
      submitButtonLabel="Save"
      backButtonPath={`/dashboard/items/${itemId}`}
    />
  );
}

export default function ItemEdit() {
  const { itemId } = useParams();

  const [getItemById] = useLazyQuery(
    GET_ITEM_BY_ID_QUERY);

  const [updateItem] = useMutation(
    UPDATE_ITEM_MUTATION, {
    refetchQueries: [{ query: GET_ITEMS_QUERY }, { query: GET_ITEM_BY_ID_QUERY, variables: { id: +(itemId || '0') } }],
  });

  const [item, setItem] = useState<Item | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const loadData = useCallback(async () => {
    setError(null);

    const id = +(itemId || '0');

    if (id > 0) {
      setIsLoading(true);

      try {
        const { data, error: getItemIdError } = await getItemById({ variables: { id } });

        setIsLoading(false);
        setError(null);

        if (getItemIdError) {
          setError(getItemIdError);
        }

        if (data?.item) {
          setItem(data.item);
        } else {
          setError({ message: 'No item found' } as Error);
        }

      } catch (showDataError) {
        setError(showDataError as Error);
      }
    }
    setIsLoading(false);
  }, [getItemById, itemId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleSubmit = useCallback(
    async (id: number, formValues: Partial<ItemFormState['values']>) => {
      setIsLoading(true);

      const { data: result } = await updateItem({
        variables: {
          id: id,
          input: {
            id: id,
            name: formValues.name || '',
            unitType: formValues.unitType || UnitType.POUND,
            isIngredient: !!formValues.isIngredient,
            description: formValues.description,
            orderNotes: formValues.orderNotes,
            standardShippingQuantity: +(formValues.standardShippingQuantity || 0),
            minOrderQuantity: +(formValues.minOrderQuantity || 0),
            maxOrderQuantity: +(formValues.maxOrderQuantity || 0)
          }
        }
      });

      setIsLoading(false);

      if (result?.updatedItem) {
        setItem(result.updatedItem);
        return true;
      }

      return false;
    },
    [updateItem],
  );

  const renderEdit = useMemo(() => {
    if (isLoading) {
      return (
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            m: 1,
          }}
        >
          <CircularProgress />
        </Box>
      );
    }
    if (error) {
      return (
        <Box sx={{ flexGrow: 1 }}>
          <Alert severity="error">{error.message}</Alert>
        </Box>
      );
    }

    return item ? (
      <ItemEditForm initialValues={item} onSubmit={handleSubmit} />
    ) : null;
  }, [isLoading, error, item, handleSubmit]);

  return (
    <PageContainer
      title={`Edit ${item?.name}`}
      breadcrumbs={[
        { title: 'Items', path: '/dashboard/items' },
        { title: `${item?.name}`, path: `/dashboard/items/${itemId}` },
        { title: 'Edit' },
      ]}
    >
      <Box sx={{ display: 'flex', flex: 1 }}>{renderEdit}</Box>
    </PageContainer>
  );
}